<template>
  <RewardsTracker
    v-if="isExpanded"
    :tiers="tiers"
    :heading="heading"
    :size="size"
    :fill-percent="fillPercent"
    :fill-transition="fillTransition"
    :celebrating-tier-id="celebratingTierId"
    :celebration-stage="celebrationStage"
    :held-tier-ids="heldTierIds"
  />
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import { useMq } from 'vue3-mq'

import { useRewards } from '@/composables/useRewards'

import RewardsTracker from '@/components/Shared/RewardsTracker/RewardsTracker.vue'
import { RewardsTrackerSize } from '@/components/Shared/RewardsTracker/RewardsTrackerSize'

import { RewardCelebrationStage } from './RewardCelebrationStage'

/**
 * Cart-flyout placement of the `RewardsTracker`. Pulls live props from `useRewards` and renders the
 * tracker only for expanded ladders (ADR 003 — legacy catalogs show nothing). Picks the `Desktop` /
 * `Mobile` variant responsively (the flyout is a wide panel on desktop, full-width on mobile).
 * Mounted at the top of `AppCartSidebar`.
 *
 * **The unlock-sequence props pass straight through** (A2b). They are supplied by `AppCartSidebar`,
 * which owns the sequence, rather than read here: the machine's state is one run shared by the tracker,
 * the banner and the rows, so a second subscription in this component would be a second opinion about
 * what beat the flyout is on. The defaults are the resting state, so nothing else that mounts this has
 * to know the sequence exists.
 */
export default defineComponent({
  name: 'RewardsTrackerFlyout',
  components: { RewardsTracker },
  props: {
    /** Rail fill the sequence owns while it runs; `null` returns the bar to its resting derivation. */
    fillPercent: {
      type: Number as PropType<number | null>,
      default: null
    },
    /** Inline transition that wins over the tracker's resting `width` transition for the run. */
    fillTransition: {
      type: String as PropType<string | null>,
      default: null
    },
    /** The one tier celebrating, by promotion id. */
    celebratingTierId: {
      type: String as PropType<string | null>,
      default: null
    },
    /** How far that tier's node has got through the celebration. */
    celebrationStage: {
      type: String as PropType<RewardCelebrationStage>,
      default: RewardCelebrationStage.None
    },
    /** Tiers whose nodes still render pre-crossing, though their data already says unlocked. */
    heldTierIds: {
      type: Array as PropType<readonly string[]>,
      default: () => []
    }
  },
  setup() {
    const { tiers, heading, isExpanded } = useRewards()
    return { tiers, heading, isExpanded, mq$: useMq() }
  },
  computed: {
    size(): RewardsTrackerSize {
      return this.mq$.mdPlus ? RewardsTrackerSize.Desktop : RewardsTrackerSize.Mobile
    }
  }
})
</script>
